import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import './Messaging.css';

export default function Messaging({ initialTarget = null }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [thread, setThread] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const initialOpenedRef = useRef(false); // prevent double-open

  /* ── Load conversation list ── */
  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get('/messages/conversations');
      setConversations(res.data);
    } catch {
      setError('Failed to load conversations.');
    } finally {
      setLoadingConvos(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  /* ── If admin clicked "Send Message to Applicant", open that thread immediately ── */
  useEffect(() => {
    if (initialTarget && !initialOpenedRef.current) {
      initialOpenedRef.current = true;
      openThread(initialTarget);
    }
    // eslint-disable-next-line
  }, [initialTarget]);

  /* ── Scroll thread to bottom on new messages ── */
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [thread]);

  /* ── Open a thread ── */
  const openThread = async (convo) => {
    setSelectedConvo(convo);
    setLoadingThread(true);
    setError('');
    try {
      const res = await api.get(`/messages/thread/${convo.id}`);
      setThread(res.data);
      loadConversations(); // refresh unread counts
    } catch {
      setError('Failed to load messages.');
    } finally {
      setLoadingThread(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  /* ── Send a message ── */
  const sendMessage = async (e) => {
    e?.preventDefault();
    const body = newMessage.trim();
    if (!body || !selectedConvo || sending) return;
    setSending(true);
    setError('');
    try {
      await api.post('/messages/send', {
        recipient_id: selectedConvo.id,
        body,
        subject: `Message from ${user.first_name} ${user.last_name}`,
      });
      setNewMessage('');
      const res = await api.get(`/messages/thread/${selectedConvo.id}`);
      setThread(res.data);
      loadConversations();
    } catch {
      setError('Failed to send. Please try again.');
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  /* ── Helpers ── */
  const getInitials = (first = '', last = '') =>
    `${first[0] || ''}${last[0] || ''}`.toUpperCase();

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMsgTime = (dateStr) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  const statusBadge = (status) => {
    const map = { pending: 'pending', under_review: 'review', approved: 'approved', rejected: 'rejected' };
    return map[status] || 'default';
  };

  /* ════════════════════════════════════════════ RENDER ══ */
  return (
    <div className="chat-container">

      {/* ── LEFT: Conversation list ── */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h3>
            {user.role === 'admin' ? 'All Applicants' : 'Messages'}
          </h3>
          <span className="chat-sidebar-count">{conversations.length}</span>
        </div>

        {loadingConvos ? (
          <div className="chat-sidebar-loading">
            <div className="spinner" style={{ width: 24, height: 24, borderWidth: 2 }} />
          </div>
        ) : conversations.length === 0 ? (
          <div className="chat-sidebar-empty">
            <p>No conversations yet.</p>
          </div>
        ) : (
          <ul className="chat-convo-list">
            {conversations.map(convo => (
              <li
                key={convo.id}
                className={`chat-convo-item
                  ${selectedConvo?.id === convo.id ? 'active' : ''}
                  ${convo.unread_count > 0 ? 'has-unread' : ''}`}
                onClick={() => openThread(convo)}
              >
                <div className="chat-convo-avatar">
                  {getInitials(convo.first_name, convo.last_name)}
                </div>
                <div className="chat-convo-details">
                  <div className="chat-convo-top">
                    <span className="chat-convo-name">
                      {convo.first_name} {convo.last_name}
                    </span>
                    {convo.last_message_at && (
                      <span className="chat-convo-time">
                        {formatTime(convo.last_message_at)}
                      </span>
                    )}
                  </div>
                  <div className="chat-convo-bottom">
                    <span className="chat-convo-preview">
                      {convo.last_message
                        ? convo.last_message.length > 45
                          ? convo.last_message.substring(0, 45) + '…'
                          : convo.last_message
                        : user.role === 'admin' ? 'Click to start conversation' : 'No messages yet'}
                    </span>
                    {convo.unread_count > 0 && (
                      <span className="chat-unread-dot">{convo.unread_count}</span>
                    )}
                  </div>
                  {convo.application_status && (
                    <span className={`badge badge-${statusBadge(convo.application_status)} chat-status-badge`}>
                      {convo.application_status.replace('_', ' ')}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── RIGHT: Chat window ── */}
      <div className="chat-window">
        {!selectedConvo ? (
          /* Empty state */
          <div className="chat-empty-state">
            <div className="chat-empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
            </div>
            <h4>
              {user.role === 'admin'
                ? 'Select an applicant to start messaging'
                : 'Select a conversation'}
            </h4>
            <p>
              {user.role === 'admin'
                ? 'All registered applicants are listed on the left. Click any name to open or start a conversation.'
                : 'Your messages with USCIS case officers appear here.'}
            </p>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="chat-header">
              <div className="chat-header-avatar">
                {getInitials(selectedConvo.first_name, selectedConvo.last_name)}
              </div>
              <div className="chat-header-info">
                <h4>{selectedConvo.first_name} {selectedConvo.last_name}</h4>
                <span>
                  {user.role === 'admin'
                    ? selectedConvo.email
                    : 'USCIS Case Officer'}
                </span>
              </div>
              {selectedConvo.application_status && (
                <span className={`badge badge-${statusBadge(selectedConvo.application_status)} chat-header-badge`}>
                  {selectedConvo.application_status.replace('_', ' ')}
                </span>
              )}
            </div>

            {/* Messages area */}
            <div className="chat-messages" ref={threadRef}>
              {loadingThread ? (
                <div className="chat-thread-loading">
                  <div className="spinner" style={{ width: 28, height: 28, borderWidth: 2 }} />
                </div>
              ) : thread.length === 0 ? (
                <div className="chat-no-messages">
                  <div className="chat-empty-icon small">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                  </div>
                  <p>No messages yet. Send the first message below.</p>
                </div>
              ) : (
                <>
                  {thread.map((msg, idx) => {
                    const isMine = msg.sender_id === user.id;
                    const showDate = idx === 0 || (
                      new Date(msg.created_at).toDateString() !==
                      new Date(thread[idx - 1].created_at).toDateString()
                    );
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="chat-date-divider">
                            <span>
                              {new Date(msg.created_at).toLocaleDateString('en-US', {
                                weekday: 'long', month: 'long', day: 'numeric'
                              })}
                            </span>
                          </div>
                        )}
                        <div className={`chat-bubble-row ${isMine ? 'mine' : 'theirs'}`}>
                          {!isMine && (
                            <div className="chat-bubble-avatar">
                              {getInitials(msg.sender_first, msg.sender_last)}
                            </div>
                          )}
                          <div className="chat-bubble-col">
                            {!isMine && (
                              <span className="chat-bubble-name">
                                {msg.sender_first} {msg.sender_last}
                                {msg.sender_role === 'admin' && (
                                  <span className="chat-official-tag">USCIS Officer</span>
                                )}
                              </span>
                            )}
                            <div className={`chat-bubble ${isMine ? 'mine' : 'theirs'}`}>
                              <p>{msg.body}</p>
                            </div>
                            <span className="chat-bubble-time">{formatMsgTime(msg.created_at)}</span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>

            {/* Input area */}
            <div className="chat-input-area">
              {error && <p className="chat-error">{error}</p>}
              <form className="chat-input-form" onSubmit={sendMessage}>
                <textarea
                  ref={inputRef}
                  className="chat-textarea"
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                  rows={1}
                  disabled={sending}
                />
                <button
                  type="submit"
                  className={`chat-send-btn ${sending ? 'sending' : ''}`}
                  disabled={sending || !newMessage.trim()}
                  title="Send message"
                >
                  {sending ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20">
                      <circle cx="12" cy="12" r="10" strokeDasharray="40" strokeDashoffset="10"
                        style={{ animation: 'spin 0.8s linear infinite', transformOrigin: 'center' }} />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  )}
                </button>
              </form>
              <p className="chat-input-hint">Enter to send · Shift+Enter for new line</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}